import type { ReactElement } from 'react';

import {
  Checkbox,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  capitalize,
} from '@material-ui/core';

import { FilterEditorComponentProps } from '../..';
import { FilterChip } from './FilterChip';
import {
  type FilterDefinition,
  type FilterMeta,
  FilterViewerComponentProps,
  createFilter,
} from './provider';

type EnumFilterOptions<T extends string> = FilterMeta & {
  pluralLabel?: string;
  formatLabel?: (value: T) => string;
  field: string;
  enum: readonly T[];
};

const defaultFormatLabel = (value: string) => capitalize(value.toLowerCase());

export const EnumFilterViewer = <T extends string>(
  props: FilterViewerComponentProps<T[]> & {
    options: readonly T[];
    pluralLabel: string;
    formatLabel?: (value: T) => string;
  },
): ReactElement => {
  const { value, formatLabel = defaultFormatLabel } = props;
  let label: string;

  if (value.length === 0) {
    label = `${props.filter.label} is empty`;
  } else if (value.length === 1) {
    if (props.filter.id === 'state' && value[0].toLowerCase() === 'active') {
      const tempLabel = 'Active';
      label = `${props.filter.label}: ${tempLabel}`;
    } else {
      label = `${props.filter.label}: ${formatLabel(value[0])}`;
    }
  } else {
    label = `${props.pluralLabel} (${value.length})`;
  }

  return <FilterChip {...props} label={label} />;
};

export const EnumFilterEditor = <T extends string>(
  props: FilterEditorComponentProps<T[]> & {
    options: readonly T[];
    formatLabel?: (value: T) => string;
  },
): ReactElement => {
  const {
    value,
    onValueChange,
    onClose,
    options,
    formatLabel = defaultFormatLabel,
  } = props;

  return (
    <MenuList autoFocus>
      <MenuItem
        button
        dense
        disableGutters
        onClick={() => {
          onValueChange(value?.length === options.length ? [] : [...options]);
          onClose?.();
        }}
        style={{ paddingRight: 16 }}
       data-track-source-anchor="f3eab5f7c9;enum-filter-editor-menu-item;low">
        <ListItemIcon>
          <Checkbox
            edge="start"
            checked={value?.length === options.length}
            onClick={e => e.stopPropagation()}
            onChange={e => onValueChange(e.target.checked ? [...options] : [])}
            indeterminate={
              (value?.length ?? 0) > 0 &&
              (value?.length ?? 0) < (options.length ?? 0)
            }
           data-track-source-anchor="8307ef534a;enum-filter-editor-checkbox;low"/>
        </ListItemIcon>
        <ListItemText primary="All" />
      </MenuItem>

      <Divider />

      {options.map(item => (
        <MenuItem
          key={item}
          button
          dense
          onClick={() => {
            onValueChange(
              value?.includes(item)
                ? value?.filter(v => v !== item)
                : [...(value ?? []), item],
            );
            onClose?.();
          }}
          disableGutters
          style={{ paddingRight: 16 }}
         data-track-source-anchor="df7647a505;enum-filter-editor-menu-item-2;low">
          <ListItemIcon>
            <Checkbox
              edge="start"
              checked={value?.includes(item) ?? false}
              onClick={e => e.stopPropagation()}
              onChange={e => {
                if (e.target.checked) {
                  onValueChange([...(value ?? []), item]);
                } else {
                  onValueChange(value?.filter(v => v !== item) ?? []);
                }
              }}
              tabIndex={-1}
              disableRipple
             data-track-source-anchor="f42bd7763f;enum-filter-editor-checkbox-2;low"/>
          </ListItemIcon>
          <ListItemText primary={formatLabel(item)} />
        </MenuItem>
      ))}
    </MenuList>
  );
};

export const createEnumFilter = <T extends string>(
  options: EnumFilterOptions<T>,
): FilterDefinition => {
  const {
    field,
    enum: enumValues,
    pluralLabel,
    formatLabel,
    ...filterMeta
  } = options;

  return createFilter<T[]>({
    ...filterMeta,
    query: (value: T[]) => {
      return value.map(v => `${field}:${v}`).join(' OR ');
    },
    viewer: props => (
      <EnumFilterViewer
        options={enumValues}
        pluralLabel={pluralLabel ?? `${filterMeta.label}s`}
        formatLabel={formatLabel}
        {...props}
      />
    ),
    editor: props => (
      <EnumFilterEditor
        options={enumValues}
        formatLabel={formatLabel}
        {...props}
      />
    ),
  });
};
